import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    MapPinIcon,
    PhoneIcon,
    EnvelopeIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import { contactAPI } from '../../services/api';
import SEO from '../../components/SEO';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === 'phone') {
            value = value.replace(/\D/g, ''); // Restrict to numbers only
        }
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await contactAPI.create(formData);
            alert("Thank you! We will connect with you shortly.");
            setFormData({ name: '', phone: '', message: '' });
        } catch (error) {
            console.error("Error submitting form:", error);
            alert("Failed to submit. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            <SEO
                title="Contact Marvelous Agri Solution - Get Expert Advice"
                description="Contact us for agricultural consultancy, soil testing inquiries, or partnership opportunities. We are here to help you grow."
                keywords="Contact Agri Experts, Agriculture Helpline, Farming Consultancy Number, Kisan Call Center"
            />
            {/* Header Banner - BigHaat Style */}
            <section className="bg-gradient-to-r from-green-600 to-green-700 text-white py-12 md:py-16">
                <div className="container mx-auto px-6">
                    <nav className="text-sm mb-4">
                        <Link to="/" className="text-green-200 hover:text-white">Home</Link>
                        <span className="mx-2">/</span>
                        <span>Contact Us</span>
                    </nav>
                    <h1 className="text-3xl md:text-4xl font-bold">Contact Us</h1>
                    <p className="text-green-100 mt-2">We are here to help you grow</p>
                </div>
            </section>

            <div className="container mx-auto max-w-6xl px-4 py-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 bg-white rounded-2xl shadow-lg overflow-hidden">

                    {/* Info Side */}
                    <div className="bg-gradient-to-br from-green-600 to-green-700 p-6 md:p-10 text-white">
                        <h2 className="text-2xl font-bold mb-4">Get In Touch</h2>
                        <p className="text-green-100 mb-8 leading-relaxed">
                            Whether you have a question about our products, need agronomy advice, or want to partner with us, our team is ready to answer all your questions.
                        </p>

                        <div className="space-y-6">
                            <div className="flex items-start gap-4">
                                <div className="p-2 bg-white/10 rounded-lg">
                                    <MapPinIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="font-bold">Head Office</h4>
                                    <p className="text-green-100 text-sm">junagadh, Gujrat, India, 362001</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="p-2 bg-white/10 rounded-lg">
                                    <PhoneIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="font-bold">Call Us</h4>
                                    <a href="tel:+917859837869" className="text-green-100 text-sm hover:text-white">+91 7859837869</a>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="p-2 bg-white/10 rounded-lg">
                                    <EnvelopeIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="font-bold">Email Us</h4>
                                    <a href="mailto:info@marvelousagrisolution.com" className="text-green-100 text-sm hover:text-white break-all">info@marvelousagrisolution.com</a>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="p-2 bg-white/10 rounded-lg">
                                    <ClockIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="font-bold">Working Hours</h4>
                                    <p className="text-green-100 text-sm">Mon - Sat: 9:00 AM - 6:00 PM</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Side */}
                    <div className="p-8 md:p-10">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Send a Message</h2>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition outline-none"
                                    placeholder="Enter your full name"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition outline-none"
                                    placeholder="Enter your 10-digit mobile number"
                                    maxLength="10"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    rows="4"
                                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition outline-none resize-none"
                                    placeholder="How can we help you?"
                                    required
                                ></textarea>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-green-600 text-white py-3.5 rounded-lg font-bold hover:bg-green-700 transition shadow-lg disabled:bg-gray-400"
                            >
                                {loading ? 'Sending...' : 'Submit Request'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;
